// client/src/components/event/EventPublicSettings.jsx
//
// Publish control for an event's first-class public page (On-Screen 2.0).
// Route: /:tenant/:eventId/public-page  (linked from the Event Hub).

import React, { useCallback, useEffect, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import axios from 'axios';
import { useTranslation } from 'react-i18next';
import { FaArrowLeft, FaHome, FaExternalLinkAlt, FaCopy, FaCheck } from 'react-icons/fa';
import AppFooter from '../AppFooter';
import DesktopActionBar from '../DesktopActionBar';
import { updateEventPublicPage, PUBLIC_VISIBILITY, VISIBILITY_LABELS } from '../../services/publicEventApi';
import { SOCIAL_PROOF_KEYS } from '../../services/hubApi';

/** t_event.public_social_proof (JSON, or a string on some drivers) → { going: bool, … }. */
function proofSettings(raw) {
  let v = raw;
  if (typeof v === 'string') { try { v = JSON.parse(v); } catch { v = null; } }
  return Object.fromEntries(SOCIAL_PROOF_KEYS.map((k) => [k, !!(v && v[k] === true)]));
}

const baseUrl = process.env.REACT_APP_BASE_URL || '';
const appOrigin = (process.env.REACT_APP_BASE_URL || window.location.origin || '').replace(/\/+$/, '');

export default function EventPublicSettings({ tenantId: tenantProp }) {
  const { t } = useTranslation();
  const params = useParams();
  const tenantId = tenantProp || params.tenant;
  const { eventId } = params;
  const navigate = useNavigate();

  const [event, setEvent] = useState(null);
  const [tenantSlug, setTenantSlug] = useState(null);
  const [form, setForm] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');
  const [savedMsg, setSavedMsg] = useState('');
  const [copied, setCopied] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const [{ data: ev }, { data: t }] = await Promise.all([
        axios.get(`${baseUrl}/api/events/${tenantId}/events/${eventId}`),
        axios.get(`${baseUrl}/api/tenants/${tenantId}`).catch(() => ({ data: {} })),
      ]);
      const e = ev.event || {};
      setEvent(e);
      setTenantSlug(t?.tenant?.slug || t?.slug || null);
      setForm({
        visibility: e.public_visibility || 'private',
        slug: e.public_slug || '',
        headline: e.public_headline || '',
        ctaLabel: e.public_cta_label || '',
        ctaUrl: e.public_cta_url || '',
        venueName: e.venue_name || '',
        venueAddress: e.venue_address || '',
        venueLat: e.venue_lat ?? '',
        venueLng: e.venue_lng ?? '',
        socialProof: proofSettings(e.public_social_proof),
      });
    } catch (err) {
      setError(err?.response?.data?.message || 'Failed to load event');
    } finally {
      setLoading(false);
    }
  }, [tenantId, eventId]);

  useEffect(() => { load(); }, [load]);

  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  const save = async () => {
    setSaving(true);
    setError('');
    setSavedMsg('');
    try {
      const res = await updateEventPublicPage(tenantId, eventId, form);
      const p = res.public || {};
      setEvent((e) => ({ ...e, ...{
        public_visibility: p.public_visibility, public_slug: p.public_slug,
        public_headline: p.public_headline, public_cta_label: p.public_cta_label,
        public_cta_url: p.public_cta_url, venue_name: p.venue_name, venue_address: p.venue_address,
        venue_lat: p.venue_lat, venue_lng: p.venue_lng,
      } }));
      if (p.public_social_proof) setForm((f) => ({ ...f, socialProof: proofSettings(p.public_social_proof) }));
      setForm((f) => ({ ...f, slug: p.public_slug || f.slug }));
      setSavedMsg(p.public_visibility === 'private' ? 'Public page turned off' : 'Public page saved');
    } catch (err) {
      setError(err?.response?.data?.message || err.message || 'Save failed');
    } finally {
      setSaving(false);
    }
  };

  const copyUrl = async (url) => {
    if (!url) return;
    try {
      await navigator.clipboard.writeText(url);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    } catch { /* clipboard unavailable */ }
  };

  const navButtons = [
    { icon: <FaArrowLeft />, label: 'Back', onClick: () => navigate(`/${tenantId}/${eventId}/hub`) },
    { icon: <FaHome />, label: 'Home', onClick: () => navigate(`/${tenantId}/dashboard`) },
  ];
  const footer = <AppFooter buttons={navButtons} />;
  const actionBar = <DesktopActionBar buttons={navButtons} />;

  if (loading) return <div className="p-6 text-sm opacity-70" style={{ paddingBottom: 72 }}>{actionBar}Loading…{footer}</div>;
  if (!form) return <div className="p-6 text-sm text-red-600" style={{ paddingBottom: 72 }}>{actionBar}{error}{footer}</div>;

  const liveUrl = form.slug && tenantSlug ? `${appOrigin}/ev/${tenantSlug}/${form.slug}` : null;
  const isLive = (event?.public_visibility || 'private') !== 'private';

  const field = 'w-full rounded border border-black/15 bg-transparent px-2 py-1.5 text-sm dark:border-white/15';

  return (
    <div className="mx-auto max-w-2xl space-y-4 p-4" style={{ paddingBottom: 72 }}>
      {actionBar}
      <div>
        <h1 className="text-2xl font-semibold">Public event page</h1>
        <p className="text-sm opacity-60">{event?.name}</p>
      </div>

      {error && <div className="text-sm text-red-600">{error}</div>}
      {savedMsg && <div className="rounded bg-emerald-100 px-3 py-1.5 text-sm text-emerald-800 dark:bg-emerald-900/50 dark:text-emerald-200">{savedMsg}</div>}

      <section className="space-y-2 rounded-lg border border-black/10 p-4 dark:border-white/10">
        <label className="block text-xs font-semibold uppercase tracking-wide opacity-60">Visibility</label>
        {PUBLIC_VISIBILITY.map((v) => (
          <label key={v} className="flex items-center gap-2 text-sm">
            <input type="radio" name="vis" checked={form.visibility === v} onChange={() => set('visibility', v)} />
            {VISIBILITY_LABELS[v]}
          </label>
        ))}

        <label className="mt-2 block text-xs opacity-70">URL slug</label>
        <div className="flex items-center gap-1 text-sm">
          <span className="opacity-50">/ev/{tenantSlug || 'your-org'}/</span>
          <input className={field} value={form.slug} placeholder="auto from event name"
            onChange={(e) => set('slug', e.target.value)} />
        </div>
        {liveUrl && (
          <div className="flex flex-wrap items-center gap-2 text-xs">
            {isLive ? (
              <a href={liveUrl} target="_blank" rel="noreferrer" className="inline-flex items-center gap-1 text-cobalt underline">
                <FaExternalLinkAlt size={10} /> {liveUrl}
              </a>
            ) : (
              <span className="break-all opacity-60">{liveUrl}</span>
            )}
            <button
              type="button"
              onClick={() => copyUrl(liveUrl)}
              className="inline-flex items-center gap-1 rounded border border-black/15 px-2 py-1 dark:border-white/15"
            >
              {copied ? <FaCheck size={10} /> : <FaCopy size={10} />}
              {copied ? 'Copied!' : 'Copy URL'}
            </button>
          </div>
        )}
      </section>

      <section className="space-y-2 rounded-lg border border-black/10 p-4 dark:border-white/10">
        <div className="text-xs font-semibold uppercase tracking-wide opacity-60">Page content</div>
        <input className={field} placeholder="Hero headline (optional)" value={form.headline} onChange={(e) => set('headline', e.target.value)} />
        <div className="flex gap-2">
          <input className={field} placeholder="Register button label" value={form.ctaLabel} onChange={(e) => set('ctaLabel', e.target.value)} />
          <input className={field} placeholder="Register button URL" value={form.ctaUrl} onChange={(e) => set('ctaUrl', e.target.value)} />
        </div>
      </section>

      <section className="space-y-2 rounded-lg border border-black/10 p-4 dark:border-white/10">
        <div className="text-xs font-semibold uppercase tracking-wide opacity-60">Venue</div>
        <input className={field} placeholder="Venue name" value={form.venueName} onChange={(e) => set('venueName', e.target.value)} />
        <input className={field} placeholder="Address" value={form.venueAddress} onChange={(e) => set('venueAddress', e.target.value)} />
        <div className="flex gap-2">
          <input className={field} placeholder="Latitude (optional)" value={form.venueLat} onChange={(e) => set('venueLat', e.target.value)} />
          <input className={field} placeholder="Longitude (optional)" value={form.venueLng} onChange={(e) => set('venueLng', e.target.value)} />
        </div>
      </section>

      <section className="space-y-2 rounded-lg border border-black/10 p-4 dark:border-white/10">
        <div className="text-xs font-semibold uppercase tracking-wide opacity-60">{t('eventPublic.proof.title', 'Social proof')}</div>
        <p className="text-xs opacity-70">
          {t('eventPublic.proof.help', 'Choose what the public page may show. Everything is off until you turn it on.')}
        </p>
        {SOCIAL_PROOF_KEYS.map((k) => (
          <label key={k} className="flex items-start gap-2 text-sm">
            <input
              type="checkbox"
              className="mt-1"
              checked={!!form.socialProof?.[k]}
              onChange={(e) => set('socialProof', { ...form.socialProof, [k]: e.target.checked })}
            />
            <span>
              {{
                going: t('eventPublic.proof.going', 'How many are going (tickets + registrations)'),
                entries: t('eventPublic.proof.entries', 'Number of raffle, contest and challenge entries'),
                sellingFast: t('eventPublic.proof.sellingFast', '"Selling fast" badge when tickets are moving'),
                spotsLeft: t('eventPublic.proof.spotsLeft', '"N spots left" once few remain'),
                attendees: t('eventPublic.proof.attendees', '"Who\'s going": first name and photo of buyers who opt in at checkout'),
              }[k]}
            </span>
          </label>
        ))}
      </section>

      <div className="flex justify-end gap-2">
        <button onClick={save} disabled={saving} className="rounded bg-cobalt px-4 py-2 text-sm text-white disabled:opacity-50">
          {saving ? 'Saving…' : (form.visibility === 'private' ? 'Save (page off)' : 'Publish')}
        </button>
      </div>

      {footer}
    </div>
  );
}
